
import { supabase } from '../client';

// Type for select options
export interface SelectOptions {
  single?: boolean;
  limit?: number;
  order?: { column: string; ascending: boolean };
  match?: Record<string, any>;
}

// For select operations with proper typing
export async function safeSelect<T = any>(
  tableName: string, 
  columns: string = '*',
  match: Record<string, any> = {},
  options: SelectOptions = {}
): Promise<T[]> {
  try {
    console.log(`Making select query to table: ${tableName}`);
    
    // Build the query step by step using type assertion to bypass strict typing
    let query = (supabase as any).from(tableName).select(columns);
    
    // Apply match conditions
    Object.entries(match).forEach(([column, value]) => {
      query = query.eq(column, value);
    });
    
    if (options.limit) {
      query = query.limit(options.limit);
    }
    
    if (options.order) {
      query = query.order(options.order.column, { ascending: options.order.ascending });
    }
    
    if (options.single) {
      const { data, error } = await query.maybeSingle();
      if (error) {
        console.error(`Error selecting from ${tableName}:`, error);
        return [] as T[];
      }
      return data ? [data as T] : [] as T[];
    }
    
    const { data, error } = await query;
    if (error) {
      console.error(`Error selecting from ${tableName}:`, error);
      return [] as T[];
    }
    
    return (data || []) as T[];
  } catch (err) {
    console.error(`Exception in safeSelect for ${tableName}:`, err);
    return [] as T[];
  }
}

// For insert operations with proper typing
export async function safeInsert<T = any>(
  tableName: string,
  values: any
): Promise<T | null> {
  try {
    console.log(`Making insert query to table: ${tableName}`);
    
    const { data, error } = await (supabase as any)
      .from(tableName)
      .insert(values)
      .select();
      
    if (error) {
      console.error(`Error inserting into ${tableName}:`, error);
      return null;
    }
    
    if (!data || data.length === 0) {
      return null;
    }
    
    return data[0] as T;
  } catch (err) {
    console.error(`Exception in safeInsert for ${tableName}:`, err);
    return null;
  }
}

// For update operations with proper typing
export async function safeUpdate<T = any>(
  tableName: string,
  values: any,
  match: Record<string, any>
): Promise<T | null> {
  try {
    console.log(`Making update query to table: ${tableName}`);
    
    let query = (supabase as any)
      .from(tableName)
      .update(values);
      
    Object.entries(match).forEach(([column, value]) => {
      query = query.eq(column, value);
    });
    
    const { data, error } = await query.select();
    
    if (error) {
      console.error(`Error updating ${tableName}:`, error);
      return null;
    }
    
    if (!data || data.length === 0) {
      return null;
    }
    
    return data[0] as T;
  } catch (err) {
    console.error(`Exception in safeUpdate for ${tableName}:`, err);
    return null;
  }
}
